import MailIcon from '../../../assets/icons/mail.svg?react';
import PlusIcon from '../../../assets/icons/plus.svg?react';
import TeamsIcon from '../../../assets/icons/teams.svg?react';
import { ChannelType } from '../../../gql/generated';
import { Input } from '../../Input';
import { ChannelFormData } from './context';

interface ChannelFormFieldsProps {
  formData: ChannelFormData;
  updateFormData: (updates: Partial<ChannelFormData>) => void;
}

/**
 * Reusable channel form fields component.
 * Can be used with or without the AddChannelDialog context.
 */
export function ChannelFormFields({
  formData,
  updateFormData,
}: ChannelFormFieldsProps) {
  const channelTypes = [
    {
      type: ChannelType.Email,
      label: 'Mail',
      icon: MailIcon,
    },
    {
      type: ChannelType.Teams,
      label: 'Teams',
      icon: TeamsIcon,
    },
    {
      type: ChannelType.Grouped,
      label: 'Grouped',
      icon: PlusIcon,
    },
  ];

  return (
    <div className='flex flex-col gap-6'>
      {/* Channel Name */}
      <Input
        label='Channel Name'
        placeholder='Enter channel name'
        value={formData.name}
        onChange={(e) => updateFormData({ name: e.target.value })}
        required
      />

      {/* Channel Type Selection */}
      <div className='flex flex-col gap-1.5'>
        <label className='size-p2 font-regular text-primary'>
          Channel type
        </label>
        <div className='flex flex-col gap-2'>
          {channelTypes.map(({ type, label, icon: Icon }) => {
            const isSelected = formData.channelType === type;
            return (
              <div key={type}>
                {/* Radio Option Container */}
                <div
                  className={`flex flex-col gap-1 rounded-lg ${
                    isSelected ? 'bg-surface-primary-support p-2' : ''
                  }`}
                >
                  {/* Radio Button Row */}
                  <button
                    type='button'
                    onClick={() => updateFormData({ channelType: type })}
                    className='flex cursor-pointer items-center justify-between
                      px-2 py-3'
                  >
                    <div className='flex items-center gap-3'>
                      {/* Custom Radio Button */}
                      <div
                        className={`flex h-5 w-5 items-center justify-center
                        rounded-full border ${
                          isSelected
                            ? 'border-border-focus bg-surface-focus'
                            : 'border-border-primary bg-surface-primary'
                        }`}
                      >
                        {isSelected && (
                          <div className='h-2 w-2 rounded-full bg-white' />
                        )}
                      </div>
                      {/* Label */}
                      <span
                        className={`size-p2 ${
                          isSelected
                            ? 'text-focus font-bold'
                            : 'font-regular text-primary'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {/* Icon */}
                    <Icon
                      className={`h-6 w-6 ${
                        isSelected ? 'text-focus' : 'text-primary'
                      }`}
                    />
                  </button>

                  {/* Type-specific fields embedded in selected option */}
                  {isSelected && type === ChannelType.Email && (
                    <div className='flex items-center gap-2 px-9 pb-2'>
                      <label className='size-p2 text-primary font-regular'>
                        To
                      </label>
                      <Input
                        placeholder='email@example.com'
                        value={formData.email || ''}
                        onChange={(e) =>
                          updateFormData({ email: e.target.value })
                        }
                        className='flex-1'
                      />
                    </div>
                  )}

                  {isSelected && type === ChannelType.Teams && (
                    <div className='flex flex-col gap-2 px-9 pb-2'>
                      <Input
                        label='Webhook URL'
                        placeholder='https://outlook.office.com/webhook/...'
                        value={formData.teamsWebhookUrl || ''}
                        onChange={(e) =>
                          updateFormData({ teamsWebhookUrl: e.target.value })
                        }
                      />
                    </div>
                  )}

                  {isSelected && type === ChannelType.Grouped && (
                    <div className='flex flex-col gap-2 px-9 pb-2'>
                      <p className='size-p2 text-secondary'>
                        Select channels to group together (coming soon)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
